import { UserRole, EffectiveUser } from '@engelvoelkersamericas/common-ui';

import { IUserAccount } from '@models';
import { TeamRole } from '@configs/constants';
import { ITeamProfile } from '@stores/team-profile-store-types';

import { IAuthUser, ICheckUserAccessParams, ICheckUserAccessResult, IUserRoleWithShop, RoleCondition, Role } from './types';

/**
 * Permission keys used for access control
 */
export const PERMISSION_KEYS = {
  CAN_USE_PROFILES_FOR_OTHERS: 'can_use_profiles_for_others',
} as const;

/**
 * Permissions object structure from effectiveUser
 * Keys are either 'globals' or shopId (uppercase)
 */
export interface IEffectiveUserPermissions {
  globals?: Record<string, boolean>;
  [shopId: string]: Record<string, boolean> | undefined;
}

/**
 * Maps an effectiveUser from common-ui to the IAuthUser format used by access control utilities.
 *
 * @param {EffectiveUser | undefined} effectiveUser - The effective user from authentication store
 * @returns {IAuthUser | undefined} The mapped auth user, or undefined if no effective user
 */
export const mapEffectiveUserToAuthUser = (effectiveUser: EffectiveUser | undefined): IAuthUser | undefined => {
  if (!effectiveUser) return undefined;
  return {
    userId: effectiveUser.userId,
    roles: effectiveUser.roles,
  };
};

/**
 * Checks if the effective user has permission to view other users' profiles.
 * This checks for the 'can_use_profiles_for_others' permission either:
 * 1. Globally (in permissions.globals) - grants access to view ANY profile
 * 2. For a specific shop that the viewed user belongs to
 *
 * @param {IEffectiveUserPermissions | undefined} permissions - The permissions object from effectiveUser
 * @param {string[]} viewedUserShopIds - Array of shopIds that the viewed user belongs to
 * @returns {boolean} True if the user has permission to view the profile, false otherwise
 */
export const canUseProfilesForOthers = (permissions: IEffectiveUserPermissions | undefined, viewedUserShopIds: string[]): boolean => {
  if (!permissions) return false;

  // Check global permission first - grants access to view ANY profile
  if (permissions.globals?.[PERMISSION_KEYS.CAN_USE_PROFILES_FOR_OTHERS] === true) {
    return true;
  }

  // Check shop-specific permissions
  // The viewed user may belong to multiple shops, so check if the effective user
  // has permission for at least one of them
  return viewedUserShopIds.some((shopId) => {
    // Permissions use uppercase shopId as keys
    const upperShopId = shopId.toUpperCase();
    return permissions[upperShopId]?.[PERMISSION_KEYS.CAN_USE_PROFILES_FOR_OTHERS] === true;
  });
};

/**
 * Checks if the authenticated user has the HEAD_OFFICE role for the EVA HQ shop.
 *
 * @param {IAuthUser} authUser - The authenticated user object containing roles information
 * @returns {boolean} True if the user has the HEAD_OFFICE role for EVA HQ, false otherwise
 */
export const isAuthUserHasHeadOfficeRole = (authUser: IAuthUser): boolean => {
  return authUser?.roles.some(
    (role) => role.name.toLowerCase() === UserRole.HEAD_OFFICE.toLowerCase() && role.shopName === 'Engel & Völkers EVA HQ',
  );
};

/**
 * Map role names to their proper UserRole enum values
 *
 * @param {string} roleName - The role name to map
 * @returns {string} The mapped role name from UserRole enum
 */
export const mapRoleNameToEnum = (roleName: string): string => {
  // Convert role name to uppercase and check if it exists in UserRole enum
  const roleKey = roleName.toUpperCase() as keyof typeof UserRole;
  return roleKey in UserRole ? UserRole[roleKey] : roleName;
};

/**
 * Gets team roles for a user based on team data
 * Admin users get both TEAM_ADMIN and TEAM_MEMBER roles (hierarchical)
 *
 * @param {string} userId - The user ID to check team roles for
 * @param {ITeamProfile} teamProfile - The team profile data containing users
 * @returns {TeamRole[]} Array of team roles for the user
 */
export const getUserTeamRoles = (userId: string, teamProfile: ITeamProfile): TeamRole[] => {
  if (!teamProfile?.users) return [];

  const teamUser = teamProfile.users.find((user) => user.userId.toLowerCase() === userId.toLowerCase());

  if (!teamUser) return [];

  if (teamUser.isAdmin || teamUser.isLeader) {
    // Team admins have both admin and member permissions
    return [TeamRole.TEAM_ADMIN, TeamRole.TEAM_MEMBER];
  }

  // Regular team members only have member permissions
  return [TeamRole.TEAM_MEMBER];
};

/**
 * Evaluates a single role condition.
 *
 * @param {RoleCondition} condition - The role condition to evaluate
 * @param {IUserRoleWithShop[]} userRoles - The user's roles to check against
 * @returns {boolean} True if the condition is met, false otherwise
 */
export const evaluateSingleCondition = (condition: RoleCondition, userRoles: IUserRoleWithShop[]): boolean => {
  // If it's a simple role condition
  if ('role' in condition) {
    const roleName = condition.role.toLowerCase();

    // Special case for HEAD_OFFICE role - must belong to 'Engel & Völkers EVA HQ' shop
    if (roleName === UserRole.HEAD_OFFICE.toLowerCase()) {
      return userRoles.some((userRole) => userRole?.name?.toLowerCase() === roleName && userRole?.shopName === 'Engel & Völkers EVA HQ');
    }

    // For all other roles, just check the role name
    return userRoles.some((userRole) => userRole?.name?.toLowerCase() === roleName);
  }

  // If it's an AND condition
  if ('and' in condition) {
    return condition.and.every((subCondition) => evaluateSingleCondition(subCondition, userRoles));
  }

  // If it's an OR condition
  if ('or' in condition) {
    return condition.or.some((subCondition) => evaluateSingleCondition(subCondition, userRoles));
  }

  return false;
};

/**
 * Evaluates complex role conditions recursively.
 * Supports nested AND/OR conditions for sophisticated access control rules.
 *
 * @param {Role[] | RoleCondition[]} conditions - The role conditions to evaluate
 * @param {IUserRoleWithShop[]} userRoles - The user's roles to check against (now includes team roles)
 * @returns {boolean} True if the conditions are met, false otherwise
 */
export const evaluateRoleConditions = (conditions: Role[] | RoleCondition[], userRoles: IUserRoleWithShop[]): boolean => {
  // If conditions is an array of strings (UserRole[]), treat it as an OR condition
  if (Array.isArray(conditions) && conditions.length > 0 && typeof conditions[0] === 'string') {
    const lowerCaseRoles = (conditions as string[]).map((role) => role.toLowerCase());
    // Check each user role against the required roles
    return userRoles.some((userRole) => {
      const userRoleName = userRole?.name?.toLowerCase();

      // If this is a HEAD_OFFICE role, make sure it's from the EVA HQ shop
      if (userRoleName === UserRole.HEAD_OFFICE.toLowerCase()) {
        return lowerCaseRoles.includes(userRoleName) && userRole?.shopName === 'Engel & Völkers EVA HQ';
      }

      // For all other roles, just check if the role name is in the required roles
      return lowerCaseRoles.includes(userRoleName);
    });
  }

  // If conditions is an array of objects, evaluate each condition
  if (Array.isArray(conditions)) {
    // Default to OR behavior for arrays of conditions
    return conditions.some((condition) => evaluateSingleCondition(condition as RoleCondition, userRoles));
  }

  // If it's a single condition
  return evaluateSingleCondition(conditions as RoleCondition, userRoles);
};

/**
 * Central permission checking function that determines if a user has access to content
 * based on their roles (from effectiveUser).
 *
 * After the auth refactor, all permission checks use authUser (effectiveUser) roles.
 * The contextSelectedUserAccount is only used to filter roles by relevant shops.
 *
 * @param {Role[] | RoleCondition[]} requiredRoles - Roles or conditions required to access the content
 * @param {IUserAccount} contextSelectedUserAccount - The user account being accessed (used for shop filtering)
 * @param {IAuthUser} authUser - The authenticated user object (effectiveUser)
 * @param {ITeamProfile} [teamProfile] - Optional team profile data to determine team roles
 * @param {'user' | 'team'} [context='user'] - Context for role checking (user or team)
 * @returns {boolean} True if the user has permission to access the content, false otherwise
 */
export const hasUserRequiredRoles = (
  requiredRoles: Role[] | RoleCondition[],
  contextSelectedUserAccount: IUserAccount,
  authUser: IAuthUser,
  teamProfile?: ITeamProfile,
  context: 'user' | 'team' = 'user',
): boolean => {
  if (requiredRoles == null) return false;

  // If no roles are required, allow access
  if (requiredRoles.length === 0) return true;

  // For team context, require shopId to be present
  if (context === 'team' && !teamProfile?.shopId) {
    return false;
  }

  let authUserRoles: IUserRoleWithShop[] = [];

  if (context === 'team') {
    // For team context, only include authUser roles from the team's shop
    authUserRoles =
      authUser?.roles
        ?.filter((role) => role.shopId?.toLowerCase() === teamProfile?.shopId?.toLowerCase())
        ?.map((role) => ({
          ...role,
          name: mapRoleNameToEnum(role.name),
          shopName: role.shopName,
        })) || [];
  } else {
    // For user context, filter authUser roles by shops the viewed user belongs to
    authUserRoles =
      authUser?.roles
        ?.filter((role) => contextSelectedUserAccount?.shops?.find((shop) => shop.shopId?.toLowerCase() === role.shopId?.toLowerCase()))
        ?.map((role) => ({
          ...role,
          name: mapRoleNameToEnum(role.name),
          shopName: role.shopName,
        })) || [];
  }

  // If the authenticated user has the HEAD_OFFICE role, add it
  // This grants access even if the user doesn't have roles in the viewed user's shops
  if (isAuthUserHasHeadOfficeRole(authUser)) {
    authUserRoles.push({ name: UserRole.HEAD_OFFICE, shopName: 'Engel & Völkers EVA HQ' });
  }

  // Add team roles for the authenticated user (effectiveUser)
  if (teamProfile) {
    const authUserTeamRoles = getUserTeamRoles(authUser.userId || '', teamProfile);
    authUserTeamRoles.forEach((teamRole) => {
      authUserRoles.push({ name: teamRole, shopName: 'Team' });
    });
  }

  if (!authUserRoles?.length) return false;

  return evaluateRoleConditions(requiredRoles, authUserRoles);
};

/**
 * Determines if a user has access based on the current profile mode and required roles.
 * This is a shared utility function used by both the useRoleAccess hook and ProtectedRoute component.
 *
 * @param {ICheckUserAccessParams} params - The parameters for checking access
 * @returns {ICheckUserAccessResult} Result object with hasAccess flag
 */
export const checkUserAccess = ({
  userRequiredRoles,
  otherUserRequiredRoles,
  contextSelectedUserAccount,
  authUser,
  isOtherUserProfile,
  baseRoles,
  useDefaultRoles = true,
  teamProfile,
  context = 'user',
}: ICheckUserAccessParams): ICheckUserAccessResult => {
  // Determine which roles to check based on the current profile mode
  const requiredRoles = isOtherUserProfile ? otherUserRequiredRoles : userRequiredRoles;

  // If no specific roles are provided and useDefaultRoles is true, use the baseRoles
  const rolesToCheck = requiredRoles ?? (useDefaultRoles ? baseRoles : null);

  // If roles are empty, default to allowing access
  if (rolesToCheck?.length === 0) {
    return { hasAccess: true };
  }

  // If there are no roles to check, deny access
  if (rolesToCheck === null) {
    return { hasAccess: false };
  }

  // Check if the user has the required roles (always uses authUser/effectiveUser roles)
  const hasAccess = hasUserRequiredRoles(rolesToCheck, contextSelectedUserAccount, authUser, teamProfile, context);

  return { hasAccess };
};
